import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, WritableSignal, computed, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppFacadeService } from '../../services/app-facade.service';
import { Consumer } from '../../admin/models/consumer-model';
import { CellStyle, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { ContactResponse } from '../../admin/interfaces/find-email.interface';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { formatDateType } from '../../types';
import { Filter, FilterOutput } from '../../shared/models/filters.model';
import { ContactFilterStatus } from '../../enums/contact-filter-status.enum';
import { RoleCheckboxRendererComponent } from './renderer/role-checkbox.component';
import { AssignUserRoleRequest } from '../../admin/interfaces/user-accout.interface';
import { UserAccountFeature } from '../../enums/user-account-feature.enum';
import { AgGridCustomHeaderComponent } from '../../shared/components/ag-grid-header/ag-grid-header.component';
import { Customer } from '../../models/consumer';
import { AgGridService } from '../../services/ag-grid.service';

@Component({
    standalone: false,
    selector: 'por-contacts',
    templateUrl: './contacts.component.html',
    styleUrls: ['./contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactsComponent implements OnInit {
    private readonly translateService = inject(TranslateService);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly datePipe = inject(FormatDatePipe);
    private readonly agGridService = inject(AgGridService);

    @Input() consumer!: Consumer | Customer | null;
    @Input() set consumerContacts(data: ContactResponse[]) {
        this.allContacts.set(data || []);
    }

    @Output() readonly closePanel = new EventEmitter<boolean>(false);

    columnDefs: ColDef[] = [];
    defaultColDef: ColDef = {
        sortable: true,
        suppressMovable: true,
        resizable: false,
        wrapText: true,
        sortingOrder: ['asc', 'desc'],
        suppressMenu: true,
        flex: 1
    };

    gridOptions: GridOptions = {
        suppressContextMenu: true,
        frameworkComponents: {
            roleCheckboxRenderer: RoleCheckboxRendererComponent
        }
    };

    public readonly context: { componentParent: ContactsComponent };

    private gridApi!: GridApi;

    // Signals for data and filter state
    readonly allContacts: WritableSignal<ContactResponse[]> = signal([]);
    readonly activeFilters: WritableSignal<string[]> = signal([ContactFilterStatus.Active]);
    readonly initFilters = [ContactFilterStatus.Active];
    readonly filteredContacts = computed(() => {
        const contacts = this.allContacts();
        const filters = this.activeFilters();

        if (filters.length === 0 || filters.includes(ContactFilterStatus.ShowAll)) {
            return contacts;
        }

        return contacts.filter(contact => {
            const includeIfRegistered = !filters.includes(ContactFilterStatus.IsRegisterOnPortal) || contact.isRegisterOnPortal;

            const includeIfActive = !filters.includes(ContactFilterStatus.Active) || !contact.inactiveDateTime || new Date(contact.inactiveDateTime) > new Date();

            return includeIfRegistered && includeIfActive;
        });
    });

    filterValues: Filter[] = [];

    roleColumns: UserAccountFeature[] = Object.values(UserAccountFeature) as UserAccountFeature[];

    constructor() {
        this.context = {
            componentParent: this
        };
    }

    ngOnInit(): void {
        this.filterValues = [
            {
                label: this.translateService.instant('RegisteredOnPortal'),
                value: ContactFilterStatus.IsRegisterOnPortal
            },
            {
                label: this.translateService.instant('Active'),
                value: ContactFilterStatus.Active
            },
            {
                label: this.translateService.instant('showAll'),
                value: ContactFilterStatus.ShowAll
            }
        ];

        this.columnDefs = [
            {
                field: 'name',
                headerName: this.translateService.instant('ContactName'),
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header',
                minWidth: 155,
                wrapText: true
            },
            {
                field: 'email',
                headerName: this.translateService.instant('GrayNotRegisterOnPortal'),
                cellStyle: (params: { value: string; data: ContactResponse }) => {
                    let css: CellStyle = {
                        textAlign: 'left'
                    };
                    if (!params.data.isRegisterOnPortal) {
                        css = {
                            ...css,
                            backgroundColor: '#f5f5f5'
                        };
                    }
                    return css;
                },
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header',
                maxWidth: 280,
                wrapText: true
            },
            {
                field: 'phone',
                headerName: this.translateService.instant('Phone'),
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header'
            },
            {
                field: 'lastLoginDateTime',
                headerName: this.translateService.instant('LastLogin'),
                cellRenderer: this.formatDate,
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header',
                wrapText: true
            },
            ...this.roleColumns.map(role => ({
                headerName: this.translateService.instant(`${role}`),
                headerComponent: AgGridCustomHeaderComponent,
                headerComponentParams: {
                    isShowTooltip: true,
                    tooltipTitle: this.translateService.instant(`${role}.TooltipTitle`),
                    tooltipContent: this.translateService.instant(`${role}.TooltipContent`)
                },
                field: 'roles',
                cellRenderer: 'roleCheckboxRenderer',
                cellRendererParams: {
                    roleKey: `${role}`
                },
                wrapText: true,
                editable: false
            })),
            {
                field: 'inactiveDateTime',
                headerName: this.translateService.instant('Inactive'),
                cellRenderer: this.formatDate,
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header'
            }
        ];
    }

    private readonly formatDate = (params: formatDateType) => {
        const date = this.datePipe.transform(params.value, this.appFacadeService.featureToggleService.isSyrinx());
        return `<span class="theme-table-row">${date}</span>`;
    };

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;
        this.gridApi.setDomLayout('autoHeight');
        params.columnApi?.autoSizeAllColumns();
        this.agGridService.setHorizontalScrollBar();
    }

    onCloseButtonClick() {
        this.closePanel.emit(true);
    }

    filter($e: FilterOutput) {
        const filters = $e.multifilters || [];
        this.activeFilters.set(filters);
    }

    onCheckboxChange(data: AssignUserRoleRequest): void {
        this.appFacadeService.assignOrRevokeRole(data);
    }
}
